export type ZodiacSign =
  | 'Aries' | 'Taurus' | 'Gemini' | 'Cancer' | 'Leo' | 'Virgo' |'Libra' | 'Scorpio' | 'Sagittarius' | 'Capricorn' | 'Aquarius' | 'Pisces';

export type Element = 'Fire' | 'Earth' | 'Air' | 'Water';
export type Planet = 'Sun' | 'Moon' | 'Mercury' | 'Venus' | 'Mars' | 'Jupiter' | 'Saturn';

export interface BirthChart {
  sunSign: ZodiacSign;
  moonSign: ZodiacSign;
  risingSign: ZodiacSign;
  lifePathNumber: number;
  destinyNumber: number;
  dominantElement: Element;
  elementBreakdown: {
    fire: number;
    earth: number;
    air: number;
    water: number;
  };
}

export interface UserProfile {
  id: string;
  name: string;
  age: number;
  location: string;
  photos: string[];
  bio: string;
  occupation: string;
  chart: BirthChart;
  planetaryPlacements: { planet: Planet; sign: ZodiacSign }[];
  interests: string[];
  lookingFor: string;
}

export interface CompatibilityScore {
  overall: number;
  emotional: number;
  intellectual: number;
  physical: number;
  spiritual: number;
  tags: string[];
  narrative: string;
  planetaryAspects: { aspect: string; planets: string; intensity: 'Strong' | 'Moderate' | 'Mild' }[];
}

export const ZODIAC_SYMBOLS: Record<ZodiacSign, string> = {
  Aries: '♈', Taurus: '♉', Gemini: '♊', Cancer: '♋',
  Leo: '♌', Virgo: '♍', Libra: '♎', Scorpio: '♏',
  Sagittarius: '♐', Capricorn: '♑', Aquarius: '♒', Pisces: '♓',
};

export const ELEMENT_FOR_SIGN: Record<ZodiacSign, Element> = {
  Aries: 'Fire', Leo: 'Fire', Sagittarius: 'Fire',
  Taurus: 'Earth', Virgo: 'Earth', Capricorn: 'Earth',
  Gemini: 'Air', Libra: 'Air', Aquarius: 'Air',
  Cancer: 'Water', Scorpio: 'Water', Pisces: 'Water',
};

export const ELEMENT_COLORS: Record<Element, string> = {
  Fire: '#EF4444',
  Earth: '#84CC16',
  Air: '#60A5FA',
  Water: '#06B6D4',
};

export const ELEMENT_EMOJIS: Record<Element, string> = {
  Fire: '🔥',
  Earth: '🌿',
  Air: '💨',
  Water: '💧',
};

// The current user (logged in)
export const CURRENT_USER: UserProfile = {
  id: 'user-current',
  name: 'Arjun Mehta',
  age: 29,
  location: 'Mumbai, India',
  photos: [
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=500&fit=crop',
  ],
  bio: 'Seeking a soul who reads the stars as fluently as they read poetry. Believer in cosmic timing and meaningful conversations over chai.',
  occupation: 'Product Designer',
  chart: {
    sunSign: 'Scorpio',
    moonSign: 'Pisces',
    risingSign: 'Cancer',
    lifePathNumber: 7,
    destinyNumber: 3,
    dominantElement: 'Water',
    elementBreakdown: { fire: 20, earth: 15, air: 25, water: 40 },
  },
  planetaryPlacements: [
    { planet: 'Sun', sign: 'Scorpio' },
    { planet: 'Moon', sign: 'Pisces' },
    { planet: 'Mercury', sign: 'Sagittarius' },
    { planet: 'Venus', sign: 'Libra' },
    { planet: 'Mars', sign: 'Capricorn' },
  ],
  interests: ['Meditation', 'Astronomy', 'Poetry', 'Travel', 'Philosophy'],
  lookingFor: 'Deep emotional connection with someone who values introspection',
};

export const MOCK_PROFILES: UserProfile[] = [
  {
    id: 'user-001',
    name: 'Priya Sharma',
    age: 27,
    location: 'Delhi, India',
    photos: [
      'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=400&h=500&fit=crop',
      'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=400&h=500&fit=crop',
    ],
    bio: 'Cancer sun, Scorpio moon — I feel everything deeply. Looking for someone who can match my emotional depth and isn\'t afraid of real conversations at 2am.',
    occupation: 'Clinical Psychologist',
    chart: {
      sunSign: 'Cancer',
      moonSign: 'Scorpio',
      risingSign: 'Pisces',
      lifePathNumber: 9,
      destinyNumber: 6,
      dominantElement: 'Water',
      elementBreakdown: { fire: 10, earth: 20, air: 15, water: 55 },
    },
    planetaryPlacements: [
      { planet: 'Sun', sign: 'Cancer' },
      { planet: 'Moon', sign: 'Scorpio' },
      { planet: 'Mercury', sign: 'Cancer' },
      { planet: 'Venus', sign: 'Gemini' },
      { planet: 'Mars', sign: 'Pisces' },
    ],
    interests: ['Psychology', 'Tarot', 'Ocean', 'Writing', 'Yoga'],
    lookingFor: 'Someone who understands that sensitivity is a superpower',
  },
  {
    id: 'user-002',
    name: 'Kavya Nair',
    age: 26,
    location: 'Bangalore, India',
    photos: [
      'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=400&h=500&fit=crop',
    ],
    bio: 'Sagittarius with a Gemini moon — I live for adventures, ideas, and people who challenge my thinking. My chart says I need a water sign to ground me.',
    occupation: 'UX Researcher',
    chart: {
      sunSign: 'Sagittarius',
      moonSign: 'Gemini',
      risingSign: 'Aquarius',
      lifePathNumber: 5,
      destinyNumber: 8,
      dominantElement: 'Air',
      elementBreakdown: { fire: 35, earth: 10, air: 40, water: 15 },
    },
    planetaryPlacements: [
      { planet: 'Sun', sign: 'Sagittarius' },
      { planet: 'Moon', sign: 'Gemini' },
      { planet: 'Mercury', sign: 'Capricorn' },
      { planet: 'Venus', sign: 'Scorpio' },
      { planet: 'Mars', sign: 'Aries' },
    ],
    interests: ['Hiking', 'Philosophy', 'Travel', 'Podcasts', 'Astrology'],
    lookingFor: 'An intellectual companion for life\'s great adventure',
  },
  {
    id: 'user-003',
    name: 'Ananya Krishnan',
    age: 28,
    location: 'Chennai, India',
    photos: [
      'https://images.unsplash.com/photo-1488716820095-cbe80883c496?w=400&h=500&fit=crop',
    ],
    bio: 'Pisces rising, Libra sun — I create harmony wherever I go. Artist by day, stargazer by night. Seeking balance and beauty in a partner.',
    occupation: 'Visual Artist',
    chart: {
      sunSign: 'Libra',
      moonSign: 'Taurus',
      risingSign: 'Pisces',
      lifePathNumber: 6,
      destinyNumber: 2,
      dominantElement: 'Air',
      elementBreakdown: { fire: 15, earth: 30, air: 35, water: 20 },
    },
    planetaryPlacements: [
      { planet: 'Sun', sign: 'Libra' },
      { planet: 'Moon', sign: 'Taurus' },
      { planet: 'Mercury', sign: 'Virgo' },
      { planet: 'Venus', sign: 'Scorpio' },
      { planet: 'Mars', sign: 'Leo' },
    ],
    interests: ['Painting', 'Music', 'Wellness', 'Cooking', 'Literature'],
    lookingFor: 'A partner who sees beauty in the mundane',
  },
  {
    id: 'user-004',
    name: 'Meera Patel',
    age: 30,
    location: 'Pune, India',
    photos: [
      'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=400&h=500&fit=crop',
    ],
    bio: 'Leo sun, Aries moon — unapologetically passionate about everything I do. I need someone who can keep up with my fire and isn\'t intimidated by ambition.',
    occupation: 'Entrepreneur',
    chart: {
      sunSign: 'Leo',
      moonSign: 'Aries',
      risingSign: 'Sagittarius',
      lifePathNumber: 1,
      destinyNumber: 5,
      dominantElement: 'Fire',
      elementBreakdown: { fire: 60, earth: 15, air: 20, water: 5 },
    },
    planetaryPlacements: [
      { planet: 'Sun', sign: 'Leo' },
      { planet: 'Moon', sign: 'Aries' },
      { planet: 'Mercury', sign: 'Leo' },
      { planet: 'Venus', sign: 'Cancer' },
      { planet: 'Mars', sign: 'Sagittarius' },
    ],
    interests: ['Entrepreneurship', 'Dance', 'Travel', 'Fitness', 'Leadership'],
    lookingFor: 'A co-creator who matches my energy and ambition',
  },
  {
    id: 'user-005',
    name: 'Riya Desai',
    age: 25,
    location: 'Hyderabad, India',
    photos: [
      'https://images.unsplash.com/photo-1502823403499-6ccfcf4fb453?w=400&h=500&fit=crop',
    ],
    bio: 'Aquarius sun, Virgo moon — I think in systems and feel in waves. Looking for someone who appreciates both my analytical mind and my tender heart.',
    occupation: 'Data Scientist',
    chart: {
      sunSign: 'Aquarius',
      moonSign: 'Virgo',
      risingSign: 'Gemini',
      lifePathNumber: 11,
      destinyNumber: 4,
      dominantElement: 'Air',
      elementBreakdown: { fire: 20, earth: 25, air: 45, water: 10 },
    },
    planetaryPlacements: [
      { planet: 'Sun', sign: 'Aquarius' },
      { planet: 'Moon', sign: 'Virgo' },
      { planet: 'Mercury', sign: 'Aquarius' },
      { planet: 'Venus', sign: 'Capricorn' },
      { planet: 'Mars', sign: 'Libra' },
    ],
    interests: ['AI & Tech', 'Meditation', 'Reading', 'Social Impact', 'Astrology'],
    lookingFor: 'An equal — mentally stimulating and emotionally honest',
  },
  {
    id: 'user-006',
    name: 'Ishaan Verma',
    age: 31,
    location: 'Kolkata, India',
    photos: [
      'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=500&fit=crop',
    ],
    bio: 'Capricorn sun, Scorpio rising — I build things that last. Patient, loyal, and deeply intuitive beneath the stoic exterior. Seeking depth over superficiality.',
    occupation: 'Architect',
    chart: {
      sunSign: 'Capricorn',
      moonSign: 'Cancer',
      risingSign: 'Scorpio',
      lifePathNumber: 4,
      destinyNumber: 7,
      dominantElement: 'Earth',
      elementBreakdown: { fire: 10, earth: 50, air: 15, water: 25 },
    },
    planetaryPlacements: [
      { planet: 'Sun', sign: 'Capricorn' },
      { planet: 'Moon', sign: 'Cancer' },
      { planet: 'Mercury', sign: 'Sagittarius' },
      { planet: 'Venus', sign: 'Aquarius' },
      { planet: 'Mars', sign: 'Capricorn' },
    ],
    interests: ['Architecture', 'History', 'Classical Music', 'Hiking', 'Chess'],
    lookingFor: 'A lasting bond built on trust, depth, and shared values',
  },
];

// Compatibility calculation utility
export function calculateCompatibility(userA: UserProfile, userB: UserProfile): CompatibilityScore {
  const elementCompatibility: Record<Element, Record<Element, number>> = {
    Fire: { Fire: 70, Earth: 45, Air: 85, Water: 55 },
    Earth: { Fire: 45, Earth: 75, Air: 60, Water: 80 },
    Air: { Fire: 85, Earth: 60, Air: 70, Water: 65 },
    Water: { Fire: 55, Earth: 80, Air: 65, Water: 75 },
  };

  const lifePathHarmony: Record<number, number[]> = {
    1: [1, 5, 7], 2: [2, 4, 8], 3: [3, 6, 9], 4: [2, 4, 8],
    5: [1, 5, 7], 6: [3, 6, 9], 7: [1, 5, 7], 8: [2, 4, 8],
    9: [3, 6, 9], 11: [11, 22, 33], 22: [11, 22, 33], 33: [11, 22, 33],
  };

  const elemA = userA.chart.dominantElement;
  const elemB = userB.chart.dominantElement;
  const elementScore = elementCompatibility[elemA][elemB];

  const lpA = userA.chart.lifePathNumber;
  const lpB = userB.chart.lifePathNumber;
  const lifePathScore = lifePathHarmony[lpA]?.includes(lpB) ? 85 : 55;

  const moonCompatibility =
    ELEMENT_FOR_SIGN[userA.chart.moonSign] === ELEMENT_FOR_SIGN[userB.chart.moonSign] ? 90 : 60;

  const overall = Math.round((elementScore * 0.35 + lifePathScore * 0.25 + moonCompatibility * 0.4));

  const tags: string[] = [];
  if (elementScore >= 80) tags.push(`${elemA} & ${elemB} Synergy`);
  if (lifePathScore >= 80) tags.push(`Life Path ${lpA} & ${lpB} Harmony`);
  if (moonCompatibility >= 85) tags.push(`Moon Sign Resonance`);
  if (userA.chart.sunSign === userB.chart.moonSign || userB.chart.sunSign === userA.chart.moonSign)
    tags.push('Sun-Moon Conjunction');
  if (userA.chart.risingSign === userB.chart.sunSign)
    tags.push('Rising-Sun Attraction');
  if (tags.length < 3) tags.push('Karmic Soul Connection');

  const planetaryAspects: CompatibilityScore['planetaryAspects'] = [
    {
      aspect: 'Trine',
      planets: `${userA.name.split(' ')[0]}'s Sun ↔ ${userB.name.split(' ')[0]}'s Moon`,
      intensity: overall >= 75 ? 'Strong' : 'Moderate',
    },
    {
      aspect: 'Sextile',
      planets: `${userA.name.split(' ')[0]}'s Venus ↔ ${userB.name.split(' ')[0]}'s Mars`,
      intensity: 'Strong',
    },
    {
      aspect: 'Conjunction',
      planets: `${userA.name.split(' ')[0]}'s Moon ↔ ${userB.name.split(' ')[0]}'s Neptune`,
      intensity: 'Moderate',
    },
  ];

  const narrativePool = [
    `Your ${elemA.toLowerCase()} nature and ${userB.name.split(' ')[0]}'s ${elemB.toLowerCase()} energy create a powerful cosmic dance — each complementing what the other lacks.`,
    `The alignment of your Life Path ${lpA} with ${userB.name.split(' ')[0]}'s ${lpB} suggests a karmic connection forged across lifetimes.`,
    `Your moon signs speak the same emotional language, creating a rare depth of intuitive understanding between you two.`,
  ];

  return {
    overall,
    emotional: moonCompatibility,
    intellectual: Math.round(elementScore * 0.9 + 10),
    physical: Math.round(lifePathScore * 0.85 + 15),
    spiritual: Math.round((elementScore + moonCompatibility) / 2),
    tags,
    narrative: narrativePool[overall % narrativePool.length],
    planetaryAspects,
  };
}

export const PLANETARY_TRANSITS = [
  {
    id: 'transit-001',
    planet: 'Mercury',
    status: 'Retrograde',
    message: 'Mercury is in retrograde — communication may feel tangled. Choose words carefully and avoid major decisions.',
    icon: '☿',
    severity: 'warning' as const,
  },
  {
    id: 'transit-002',
    planet: 'Venus',
    status: 'in Scorpio',
    message: 'Venus enters Scorpio — deep, magnetic attraction is heightened. Intensity in connections is favored.',
    icon: '♀',
    severity: 'positive' as const,
  },
  {
    id: 'transit-003',
    planet: 'Jupiter',
    status: 'Direct in Gemini',
    message: 'Jupiter goes direct — expansion through communication. This is an auspicious time for new connections.',
    icon: '♃',
    severity: 'positive' as const,
  },
];